<template>
  <Tabs v-model:value="modelValue" :scrollable="scrollable" class="tab-parent">
    <TabList
      :style="`position: sticky; top: ${topSpace ? topSpace + 'em' : '0px'}; z-index: 1`"
      class="bg-white"
    >
      <Tab
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
        :disabled="tab.disabled"
        :data-testid="tab.value"
        class="tab-title"
        :class="{
          'p-valid-tab':
            !tab.disabled &&
            tab.validation.participatesInValidation &&
            tab.validation.hasRules &&
            tab.validation.valid,
          'p-invalid-tab':
            !tab.disabled && tab.validation.participatesInValidation && tab.validation.invalid,
          'p-disabled-tab': tab.disabled,
          'tab-title--separator': tab.separatorBefore,
        }"
      >
        <slot :name="tab.value">
          {{ tab.title }}
        </slot>
        <i
          :class="tab.iconSettings?.class"
          :style="tab.iconSettings?.style"
          @click="tab.iconSettings?.command"
        />
      </Tab>
    </TabList>
    <TabPanels class="tab-panels px-0">
      <slot />
    </TabPanels>
  </Tabs>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch, type ComputedRef } from "vue";
// eslint-disable-next-line no-restricted-imports
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import { type TabConfig, TabConfigInjectionKey, type ValidationScope } from "./TabInjectionKey";

const props = withDefaults(
  defineProps<{
    scrollable?: boolean;
    topSpace?: number;
    validationScope?: ValidationScope;
  }>(),
  {
    scrollable: true,
    topSpace: 0,
    validationScope: "all",
  },
);

const modelValue = defineModel<string>({ default: "" });
const registerTabs = ref<ComputedRef<TabConfig>[]>([]);
const validationScope = computed(() => props.validationScope);

provide(TabConfigInjectionKey, {
  registerTabs,
  activeValue: modelValue,
  validationScope,
});

const tabs = computed(() => registerTabs.value.map((tab) => tab.value));

watch(
  tabs,
  (nextTabs) => {
    const activeTab = nextTabs.find((tab) => tab.value === modelValue.value);
    if (activeTab?.disabled !== true) {
      return;
    }
    modelValue.value = nextTabs.find((tab) => !tab.disabled)?.value ?? "";
  },
  { deep: true },
);
</script>

<style lang="scss" scoped>
.p-valid-tab {
  color: var(--p-lisa-valid) !important;
}

.p-invalid-tab {
  color: var(--p-lisa-invalid) !important;
}

.p-disabled-tab {
  color: var(--p-disabled-color) !important;
}

.tab-title {
  font-size: 1em;
}

.tab-title--separator {
  border-inline-start: 1px solid var(--p-content-border-color);
  margin-inline-start: 0.5rem;
}

.tab-parent {
  max-width: 100%;
  min-width: 0;
}

.tab-parent :deep(.p-tablist-content) {
  min-width: 0;
  overflow-x: auto;
}

.tab-parent :deep(.p-tablist-tab-list) {
  min-width: max-content;
}

.tab-panels {
  max-width: 100%;
  min-width: 0;
}
</style>
